import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import PubSub, { topics } from '../../lib/pubsub';
import { printViaIframe } from '../../lib/printHelper';
import { sharedSalesEnabled } from '../../lib/crossCustomerSales';
import { shiftDateRange, fullMonthOf } from '../../lib/dateStep';
import SyncBanner from '../../components/gui/syncBanner';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import Pagination from '../../components/gui/pagination';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalAllSalesPrint from '../../components/dyrejournal/allVisitsPrint';
import DyrejournalAllSalesTable from '../../components/dyrejournal/allVisitsTable';
import DyrejournalUnconfirmedBookings from '../../components/dyrejournal/unconfirmedBookings';
import { toExclusiveEnd } from '../../lib/noShowList';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm, appAlert } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

// Pseudo sale status (like 99 = debitor list) for the «Ubekreftede timer» view:
// past bookings with no sale, served by calendarEventStore, not saleStore.
const UNCONFIRMED_STATUS = 98;

// Norwegian labels for the frontpage "Kritiske oppgaver" drill-down filters, so the
// active-filter bar can name which critical task the list is filtered on (and offer a
// reset). Mirrors the criticalTasks.* keys in no.json / criticalTasksCard.js CATALOG.
const CRITICAL_TASK_LABELS = {
    insuranceOwnerNotInvoiced: 'Forsikring oppgjort – eier ikke fakturert',
    overdueInvoices: 'Forfalte fakturaer',
    stuckIntegrationTransfers: 'Fastlåste regnskapsoverføringer',
    insuranceFollowUp: 'Forsikringskrav som venter på oppfølging',
    agingOngoingSales: 'Salg som har vært åpne for lenge',
    collectiveInvoicesDue: 'Samlefakturasalg som venter på fakturering',
    notTransferredToAccounting: 'Ikke overført til regnskap',
    voucherReportingDue: 'Bilagsrapportering forfaller',
    invoiceCreatedNotSent: 'Fakturaer opprettet, men ikke sendt',
    transferredNotSentToCustomer: 'Overført til regnskap, men ikke sendt til kunde',
};

@withText(props => ({
    saleTitle: <Text id='dyrejournal.sale-title'>Search for sale</Text>,
    searchTitle: <Text id='dyrejournal.search-title'>Search for sale</Text>,
    deleteAreYouSure: <Text id='dyrejournal.sale-delete-are-you-sure'>Are you sure you want to delete this sale?</Text>,
}))
@observer
class AllVisits extends Component {
	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('journal'),
            // currentPage: 1,
            // formatting: true,
            syncSaleNotification: null,
            bulkMarkInProgress: false,
            bulkMarkResult: null,
            visitorFilterSearch: '',
            visitorFilterResults: [],
            visitorFilterSelected: null,
        };
        this.searchTimer = null;
        this.searchInputField = null;
        this.syncSaleSubscription = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        this.setState({ loading: true });
        const { saleStore, appState } = this.props.stores;
        const { currentCustomer = {}, inputProps = {} } = appState;
        const { searchText, currentPage, showDeleted, myCustomers, cutoffDate, cutoffDateEnd, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, limit, sort, notTransferredToTripletex, notTransferredToFiken, notTransferredToDuett, tripletexTransferredNotSent, fikenTransferredNotSent, duettTransferredNotSent, excludeSubInvoices, onlySubInvoices, onlyUnpaidWeborders, criticalTask, filterIds } = saleStore;
        const { showFrontpageShop } = inputProps;

        if (activeStatus === UNCONFIRMED_STATUS) {
            await this.loadUnconfirmedBookings();
            this.setState({ loading: false });
            return;
        }

        const query = {
            search: searchText,
            showDeleted,
            myCustomers: myCustomers ? 1 : undefined,
            cutoffDate,
            cutoffDateEnd,
            paymentMethod: activePaymentMethod,
            visitor: activeVisitor,
            status: onlyUnpaidWeborders ? 2 : activeStatus,
            insuranceStatus: activeInsuranceStatus,
            user: activeUser,
            notTransferredToTripletex,
            notTransferredToFiken,
            notTransferredToDuett,
            tripletexTransferredNotSent,
            fikenTransferredNotSent,
            duettTransferredNotSent,
            excludeSubInvoices,
            onlySubInvoices,
            isWeborder: onlyUnpaidWeborders ? true : undefined,
            // When the drill-down resolved to explicit sale ids (filterIds), those ids are
            // authoritative — criticalTask is kept only as a display label and must NOT be
            // sent (some keys, e.g. insuranceOwnerNotInvoiced, have no Mongo conditions and
            // would match nothing).
            criticalTask: (Array.isArray(filterIds) && filterIds.length > 0) ? undefined : (criticalTask || undefined),
            id: filterIds || undefined,
        };
        // if (showFrontpageShop) {
        //     query.customer = currentCustomer?.id;
        // }

        await saleStore.load({
            query,
            offset: (currentPage - 1) * limit,
            limit,
            sort,
            addData: ['totalSales', 'users', 'saleUsers', 'defaultCutoffActive'],
        });
        await this.loadAllSalePayments();
        this.setState({ loading: false });
        // this.searchInputField.focus();
    }

    loadUnconfirmedBookings = async () => {
        const { saleStore, calendarEventStore } = this.props.stores;
        const { searchText, cutoffDate, cutoffDateEnd } = saleStore;
        await calendarEventStore.loadUnconfirmedBookings({
            start: cutoffDate || undefined,
            end: cutoffDateEnd ? toExclusiveEnd(cutoffDateEnd) : undefined,
            search: searchText || undefined,
        });
    }

    loadJournals = async (animalIds, sale, journalIds) => {
        const { journalStore, saleStore } = this.props.stores;
        const response = await journalStore.load({
            query: {
                id: journalIds,
                // $or: [
                //     {
                //         animal: animalIds,
                //         sale,
                //     },
                //     {
                //         id: { $in: journalIds },
                //     },
                // ],
            },
            skipUpdate: true,
            addData: ['totalSales', 'users'],
        });
        saleStore.updateKeyValue('animalJournals', response);
    }

    loadAnimals = async visitorIds => {
        const { animalStore, saleStore } = this.props.stores;
        const response = await animalStore.load({
            query: {
                owner: visitorIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAnimals', response);

        await animalStore.load({
            query: {
                owner: visitorIds,
            },
        });
    }

    loadSales = async visitorIds => {
        const { saleStore, userStore } = this.props.stores;
        await saleStore.loadVisitorSales(visitorIds, {
            query: { today: 1 },
            myCustomers: sharedSalesEnabled(userStore),
        });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('currentPayments', response);
    }

    loadAllSalePayments = async () => {
        const { salePaymentStore, saleStore } = this.props.stores;
        const { sales } = saleStore;
        const saleIds = sales.map(e => e.id);
        if (saleIds.length === 0) {
            return false;
        }
        const response = await salePaymentStore.load({
            query: {
                sale: saleIds,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('allPayments', response);
    }

    onClickPrintSales = async e => {
        const { saleStore, userStore } = this.props.stores;
        const { activeStatus, formatting } = saleStore;
        const { id } = e.target.closest('button').dataset;

        // console.log('onClickPrintSale', e);
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalAllSalesPrint
                stores={this.props.stores}
                activeStatus={activeStatus}
                formatting={formatting}
            />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    onClickToggleFormatting = e => {
        const { saleStore } = this.props.stores;
        const { formatting } = saleStore;
        saleStore.updateKeyValue('formatting', !formatting);
    }

    onClickDeleteVisit = async e => {
        e.stopPropagation();
        e.preventDefault();
        const dialogAction = beginDialogAction(this, 'onClickDeleteVisit');
        if (!dialogAction) return false;
        try {


            const { deleteAreYouSure } = this.props;
            const { id } = e.target.closest('button').dataset;
            if (((await appConfirm(deleteAreYouSure)) && dialogAction.isCurrent())) {
                const { saleStore } = this.props.stores;
                const response = await saleStore.deleteSale(parseInt(id, 10));
                if (response?.status >= 400) {
                    await appAlert(response.message || 'Salget ble ikke slettet.');
                }
                this.loadAll();
            }
        } finally {
            dialogAction.finish();
        }
    }

    // onClickViewVisit = async e => {
    //     const { id } = e.target.closest('tr').dataset;
    //     const { drawerLevel = 1 } = this.props;
	// 	const { appState, saleStore } = this.props.stores;
    //     saleStore.createNewSale({ skipAnimalJournals: true });

    //     const newSale = await saleStore.load(parseInt(id, 10), false, { skipUpdate: true });
    //     if (id) {
    //         await this.loadSalePayments(parseInt(id, 10));
    //     }
    //     if (newSale[0].visitors) {
    //         const visitorIds = newSale[0].visitors.map(e => e.id);
    //         // console.log('onClickViewVisit', visitorIds)
    //         await this.loadAnimals(visitorIds);
    //         await this.loadSales(visitorIds);
    //     }

    //     if (newSale[0].animals) {
    //         const journalIds = newSale[0].journals.map(e => e.id);
    //         const animalIds = newSale[0].animals.map(e => e.id);
    //         await this.loadJournals(animalIds, newSale[0].id, journalIds);
    //     }
    //     saleStore.updateKeyValue('newSale', newSale[0]);

    //     const { visitors, isProductionAnimal } = newSale[0];
    //     console.log('util.isDefined(isProductionAnimal)', util.isDefined(isProductionAnimal));
    //     const isProductionAnimals = util.isDefined(isProductionAnimal) ? isProductionAnimal : visitors.some(v => v?.producerNumber);
    //     saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);

    //     appState.appContainerScrollTop();
    //     appState.toggleDrawerRight(false, drawerLevel);
    // }

    searchInput = (e) => {
        // Strip commas and collapse multiple spaces (users often paste "Normann, Ola")
        const search = e.target.value.replace(/,/g, ' ').replace(/\s+/g, ' ');
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('searchText', search);
        this.setFocus();

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch();
            }, 500);
        }
    }

    saveSearchLocalStorage = (search) => {
        if (search.length <= 3) {
            return;
        }
        const searchLower = search.toLowerCase();

        const { searchResultList, keyName = '' } = this.props;
        const searchHistory = util.get(`searchHistory-allVisits`) || [];

        const updatedHistory = searchHistory.filter(term => {
            const termLower = term.toLowerCase();
            if (termLower === searchLower) {
                // Exact match, remove the old entry to re-add it at the top
                return false;
            }
            if (searchLower.startsWith(termLower)) {
                // Existing term is a prefix of the new term, remove it
                return false;
            }
            if (termLower.startsWith(searchLower)) {
                // New term is a prefix of an existing term, do not add new term
                return false;
            }
            return true; // Keep the term
        });

        // Add the new term to the top of the history
        updatedHistory.unshift(searchLower);

        // Limit list to 10 items
        if (updatedHistory.length > 10) {
            updatedHistory.shift();
        }
        util.set(`searchHistory-allVisits`, updatedHistory);
    }

    async searchFromHistory(search) {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('searchText', search);
        this.doSearch();
        this.searchInputField.focus();
    }

    async doSearch() {
        const { saleStore } = this.props.stores;
        if (saleStore.activeStatus === UNCONFIRMED_STATUS) {
            await this.loadUnconfirmedBookings();
            return;
        }
        const { searchText, currentPage, showDeleted, myCustomers, cutoffDate, cutoffDateEnd, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, limit, notTransferredToTripletex, notTransferredToFiken, notTransferredToDuett, tripletexTransferredNotSent, fikenTransferredNotSent, duettTransferredNotSent, excludeSubInvoices, onlySubInvoices, onlyUnpaidWeborders } = saleStore;

        if (searchText) {
            this.saveSearchLocalStorage(searchText);
            // console.log('doUpdateField.saveField', id, field, value);
            await saleStore.load({
                query: {
                    search: searchText,
                    showDeleted,
                    myCustomers: myCustomers ? 1 : undefined,
                    cutoffDate,
                    cutoffDateEnd,
                    status: onlyUnpaidWeborders ? 2 : activeStatus,
                    insuranceStatus: activeInsuranceStatus,
                    user: activeUser,
                    paymentMethod: activePaymentMethod,
                    visitor: activeVisitor,
                    notTransferredToTripletex,
                    notTransferredToFiken,
                    notTransferredToDuett,
                    tripletexTransferredNotSent,
                    fikenTransferredNotSent,
                    duettTransferredNotSent,
                    excludeSubInvoices,
                    onlySubInvoices,
                    isWeborder: onlyUnpaidWeborders ? true : undefined,
                },
                limit,
                skipUpdate: false,
                addData: ['totalSales', 'users', 'defaultCutoffActive'],
            });
            await this.loadAllSalePayments();
        }
    }

    onClickStatus = async e => {
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);

        const { saleStore } = this.props.stores;
        if (idInt === UNCONFIRMED_STATUS || saleStore.activeStatus === UNCONFIRMED_STATUS) {
            saleStore.updateKeyValue('activeStatus', saleStore.activeStatus === idInt ? null : idInt);
            await this.loadAll();
            return;
        }
        const { searchText, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, cutoffDate, cutoffDateEnd, limit } = saleStore;
        saleStore.updateKeyValue('activeStatus', activeStatus === idInt ? null : idInt);

        if (activeStatus === idInt) {
            const result = await saleStore.load({ query: {
                search: searchText,
                cutoffDate,
                cutoffDateEnd,
                user: activeUser,
                paymentMethod: activePaymentMethod,
                visitor: activeVisitor,
                insuranceStatus: activeInsuranceStatus,
            }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        } else {
            const result = await saleStore.load({ query: {
                status: parseInt(id, 10),
                search: searchText,
                cutoffDate,
                cutoffDateEnd,
                user: activeUser,
                paymentMethod: activePaymentMethod,
                visitor: activeVisitor,
                insuranceStatus: activeInsuranceStatus,
            }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        }
        await this.loadAllSalePayments();
    }

    onChooseStatus = async e => {
        const { value } = e.target;
        const idInt = parseInt(value, 10);

        const { saleStore } = this.props.stores;
        const { searchText, activeStatus, activeInsuranceStatus, cutoffDate, cutoffDateEnd, activeUser, activePaymentMethod, activeVisitor, limit } = saleStore;
        saleStore.updateKeyValue('activeStatus', activeStatus === idInt ? null : idInt);

        if (activeStatus === idInt) {
            const result = await saleStore.load({ query: {
                search: searchText,
                cutoffDate,
                cutoffDateEnd,
                user: activeUser,
                paymentMethod: activePaymentMethod,
                visitor: activeVisitor,
                insuranceStatus: activeInsuranceStatus,
            }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        } else {
            const result = await saleStore.load({ query: {
                status: idInt,
                search: searchText,
                cutoffDate,
                cutoffDateEnd,
                user: activeUser,
                paymentMethod: activePaymentMethod,
                visitor: activeVisitor,
                insuranceStatus: activeInsuranceStatus,
            }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        }
        await this.loadAllSalePayments();
    }

    onChooseInsuranceStatus = async e => {
        const { value } = e.target;
        const idInt = value !== '' ? parseInt(value, 10) : null;

        const { saleStore } = this.props.stores;
        const { searchText, activeStatus, activeInsuranceStatus, cutoffDate, cutoffDateEnd, activeUser, activePaymentMethod, activeVisitor, limit } = saleStore;
        saleStore.updateKeyValue('activeInsuranceStatus', idInt);

        const result = await saleStore.load({ query: {
            insuranceStatus: idInt,
            status: activeStatus,
            search: searchText,
            cutoffDate,
            cutoffDateEnd,
            user: activeUser,
            paymentMethod: activePaymentMethod,
            visitor: activeVisitor,
        }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        await this.loadAllSalePayments();
    }

    onChooseSaleUser = async e => {
        const { value } = e.target;
        const idInt = parseInt(value, 10);

        const { saleStore } = this.props.stores;
        const { searchText, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, cutoffDate, cutoffDateEnd, limit } = saleStore;
        saleStore.updateKeyValue('activeUser', idInt ? idInt : null);

        const result = await saleStore.load({ query: {
            user: idInt,
            status: activeStatus,
            insuranceStatus: activeInsuranceStatus,
            paymentMethod: activePaymentMethod,
            visitor: activeVisitor,
            search: searchText,
            cutoffDate,
            cutoffDateEnd,
        }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });

        await this.loadAllSalePayments();
    }

    onChangeBulkMarkCutoffDate = (e) => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('collectiveInvoiceCutoffDate', e.target.value);
    }

    onChangeBulkMarkCutoffDateEnd = (e) => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('collectiveInvoiceCutoffDateEnd', e.target.value);
    }

    onClickBulkMarkCollectiveInvoice = async () => {
        const dialogAction = beginDialogAction(this, 'onClickBulkMarkCollectiveInvoice');
        if (!dialogAction) return false;
        try {
            const { saleStore } = this.props.stores;
            const { collectiveInvoiceCutoffDate, collectiveInvoiceCutoffDateEnd } = saleStore;
            if (!collectiveInvoiceCutoffDate || !collectiveInvoiceCutoffDateEnd) {
                this.setState({ bulkMarkResult: { error: 'Velg fra- og til-dato' } });
                return;
            }
            if (!((await appConfirm('Er du sikker på at du vil merke alle pågående salg i denne perioden som samlefaktura?')) && dialogAction.isCurrent())) {
                return;
            }
            this.setState({ bulkMarkInProgress: true, bulkMarkResult: null });
            const response = await saleStore.bulkMarkCollectiveInvoice({
                cutoffDate: collectiveInvoiceCutoffDate,
                cutoffDateEnd: collectiveInvoiceCutoffDateEnd,
            });
            if (response?.status === 200) {
                this.setState({ bulkMarkInProgress: false, bulkMarkResult: { updatedCount: response.data?.updatedCount || 0 } });
                await this.loadAll();
            } else {
                this.setState({ bulkMarkInProgress: false, bulkMarkResult: { error: response?.message || 'Noe gikk galt' } });
            }
        } finally {
            dialogAction.finish();
        }
    }

    onChoosePaymentMethod = async e => {
        const { value } = e.target;
        const { saleStore } = this.props.stores;
        const { searchText, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, cutoffDate, cutoffDateEnd, limit } = saleStore;
        saleStore.updateKeyValue('activePaymentMethod', value ? value : null);

        const result = await saleStore.load({ query: {
            paymentMethod: value,
            visitor: activeVisitor,
            user: activeUser,
            status: activeStatus,
            insuranceStatus: activeInsuranceStatus,
            search: searchText,
            cutoffDate,
            cutoffDateEnd,
        }, limit, skipUpdate: false, addData: ['totalSales', 'users'] });
        await this.loadAllSalePayments();
    }

    onChooseLimit = async e => {
        const { value } = e.target;
        const limit = parseInt(value, 10);
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('limit', limit);
        this.loadAll();
    }

    onClickShowAll = async e => {
        const { saleStore } = this.props.stores;
        const { totalSales } = saleStore;
        const newLimit = Math.min(Math.max(totalSales || 1000, 100), 5000);
        saleStore.updateKeyValue('limit', newLimit);
        saleStore.updateKeyValue('currentPage', 1);
        this.loadAll();
    }

    clearSearch = () => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('searchText', '');
        this.loadAll();
        this.unsetFocus();
    }

    handleKeyDown = (e) => {
        const { saleStore } = this.props.stores;
        const { searchText } = saleStore;
        this.setFocus();

        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(searchText);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    setFocus = e => {
        this.setState({ focused: true });
    }

    unsetFocus = e => {
        this.setState({ focused: false });
    }

    setPage = (page) => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('currentPage', page);
        this.loadAll();
    }

    toggleShowDeleted = async e => {
        const { saleStore } = this.props.stores;
        const { showDeleted } = saleStore;
        saleStore.updateKeyValue('showDeleted', !showDeleted);
        this.loadAll();
    }

    toggleMyCustomers = async e => {
        const { saleStore } = this.props.stores;
        const { myCustomers } = saleStore;
        saleStore.updateKeyValue('myCustomers', myCustomers ? null : 1);
        this.loadAll();
    }

    toggleNotTransferredToTripletex = async e => {
        const { saleStore } = this.props.stores;
        const { notTransferredToTripletex, tripletexTransferredNotSent } = saleStore;
        const newValue = !notTransferredToTripletex;
        saleStore.updateKeyValue('notTransferredToTripletex', newValue);
        if (newValue && tripletexTransferredNotSent) {
            saleStore.updateKeyValue('tripletexTransferredNotSent', false);
        }
        this.loadAll();
    }

    toggleTripletexTransferredNotSent = async e => {
        const { saleStore } = this.props.stores;
        const { tripletexTransferredNotSent, notTransferredToTripletex } = saleStore;
        const newValue = !tripletexTransferredNotSent;
        saleStore.updateKeyValue('tripletexTransferredNotSent', newValue);
        if (newValue && notTransferredToTripletex) {
            saleStore.updateKeyValue('notTransferredToTripletex', false);
        }
        this.loadAll();
    }

    toggleNotTransferredToFiken = async e => {
        const { saleStore } = this.props.stores;
        const { notTransferredToFiken, fikenTransferredNotSent } = saleStore;
        const newValue = !notTransferredToFiken;
        saleStore.updateKeyValue('notTransferredToFiken', newValue);
        if (newValue && fikenTransferredNotSent) {
            saleStore.updateKeyValue('fikenTransferredNotSent', false);
        }
        this.loadAll();
    }

    toggleFikenTransferredNotSent = async e => {
        const { saleStore } = this.props.stores;
        const { fikenTransferredNotSent, notTransferredToFiken } = saleStore;
        const newValue = !fikenTransferredNotSent;
        saleStore.updateKeyValue('fikenTransferredNotSent', newValue);
        if (newValue && notTransferredToFiken) {
            saleStore.updateKeyValue('notTransferredToFiken', false);
        }
        this.loadAll();
    }

    toggleNotTransferredToDuett = async e => {
        const { saleStore } = this.props.stores;
        const { notTransferredToDuett, duettTransferredNotSent } = saleStore;
        const newValue = !notTransferredToDuett;
        saleStore.updateKeyValue('notTransferredToDuett', newValue);
        if (newValue && duettTransferredNotSent) {
            saleStore.updateKeyValue('duettTransferredNotSent', false);
        }
        this.loadAll();
    }

    toggleDuettTransferredNotSent = async e => {
        const { saleStore } = this.props.stores;
        const { duettTransferredNotSent, notTransferredToDuett } = saleStore;
        const newValue = !duettTransferredNotSent;
        saleStore.updateKeyValue('duettTransferredNotSent', newValue);
        if (newValue && notTransferredToDuett) {
            saleStore.updateKeyValue('notTransferredToDuett', false);
        }
        this.loadAll();
    }

    toggleExcludeSubInvoices = async e => {
        const { saleStore } = this.props.stores;
        const { excludeSubInvoices, onlySubInvoices } = saleStore;
        const newValue = !excludeSubInvoices;
        saleStore.updateKeyValue('excludeSubInvoices', newValue);
        if (newValue && onlySubInvoices) {
            saleStore.updateKeyValue('onlySubInvoices', false);
        }
        this.loadAll();
    }

    toggleOnlySubInvoices = async e => {
        const { saleStore } = this.props.stores;
        const { onlySubInvoices, excludeSubInvoices } = saleStore;
        const newValue = !onlySubInvoices;
        saleStore.updateKeyValue('onlySubInvoices', newValue);
        if (newValue && excludeSubInvoices) {
            saleStore.updateKeyValue('excludeSubInvoices', false);
        }
        this.loadAll();
    }

    toggleOnlyUnpaidWeborders = async e => {
        const { saleStore } = this.props.stores;
        const { onlyUnpaidWeborders } = saleStore;
        saleStore.updateKeyValue('onlyUnpaidWeborders', !onlyUnpaidWeborders);
        this.loadAll();
    }

    setDateRangeForMonth = (year, month) => {
        const { saleStore } = this.props.stores;
        const fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const toDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);
        this.setState({ currentYear: year, currentMonth: month });
        this.loadAll();
    }

    onClickPresetThisMonth = e => {
        const now = new Date();
        this.setDateRangeForMonth(now.getFullYear(), now.getMonth() + 1);
    }

    onClickPresetPreviousMonth = e => {
        const now = new Date();
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        this.setDateRangeForMonth(prev.getFullYear(), prev.getMonth() + 1);
    }

    onClickPresetThisYear = e => {
        const { saleStore } = this.props.stores;
        const year = (new Date()).getFullYear();
        const fromDate = `${year}-01-01`;
        const toDate = `${year}-12-31`;
        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);
        this.setState({ currentYear: year, currentMonth: 0 });
        this.loadAll();
    }

    changeCutoffDate = async e => {
        const { value: cutoffDate } = e.target;

        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('cutoffDate', cutoffDate);
        this.loadAll();
    }

    changeCutoffDateEnd = async e => {
        const { value: cutoffDateEnd } = e.target;
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('cutoffDateEnd', cutoffDateEnd);
        this.loadAll();
    }

    onClickGoToToday = e => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('cutoffDate', util.isoDate(new Date(), false, false, true));
        this.loadAll();
    }

    onClickStepDateRange = e => {
        const direction = parseInt(e.currentTarget.dataset.direction, 10);
        const { saleStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd } = saleStore;
        const next = shiftDateRange(cutoffDate, cutoffDateEnd, direction);
        if (!next) return;
        saleStore.updateKeyValue('cutoffDate', next.from || '');
        saleStore.updateKeyValue('cutoffDateEnd', next.to || '');
        // Keep the year/month selects in sync so they never show a stale month
        const month = fullMonthOf(next.from, next.to);
        this.setState({
            currentYear: month ? month.year : 0,
            currentMonth: month ? month.month : 0,
        });
        // Debounce: repeated clicks load only the final range
        clearTimeout(this.stepDateTimer);
        this.stepDateTimer = setTimeout(() => {
            this.loadAll();
        }, 500);
    }

    clearCutoffDate = e => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('cutoffDate', '');
        // Also reset year/month selects since they control the dates
        this.setState({ currentYear: 0, currentMonth: 0 });
        this.loadAll();
    }

    clearCutoffDateEnd = e => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('cutoffDateEnd', '');
        // Also reset year/month selects since they control the dates
        this.setState({ currentYear: 0, currentMonth: 0 });
        this.loadAll();
    }

    onClearAllFilters = () => {
        const { saleStore } = this.props.stores;
        saleStore.clearAllFilters();
        // Reset local component state for year/month selects (0 matches default option value)
        this.setState({
            currentYear: 0,
            currentMonth: 0,
        });
        this.loadAll();
    }

    changeYear = e => {
        const { saleStore } = this.props.stores;
        // set fromDate and toDate to the selected year and month
        const currentYear = parseInt(e.target.value, 10);
        let { currentMonth } = this.state;
        if (!currentMonth) {
            currentMonth = 1;
        }
        const fromDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const toDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(new Date(currentYear, currentMonth, 0).getDate()).padStart(2, '0')}`;

        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);

        this.setState({ currentYear }, () => {
            this.loadTimer = setTimeout(() => {
                this.loadAll();
            }, 700);
        });
    }

    changeMonth = e => {
        const { saleStore } = this.props.stores;
        // set fromDate and toDate to the selected year and month
        const currentMonth = parseInt(e.target.value, 10);
        let { currentYear } = this.state;
        if (!currentYear) {
            currentYear = (new Date()).getFullYear();
        }
        const fromDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const toDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(new Date(currentYear, currentMonth, 0).getDate()).padStart(2, '0')}`;

        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);

        this.setState({ currentMonth }, () => {
            this.loadTimer = setTimeout(() => {
                this.loadAll();
            }, 700);
        });
    }

    setDropdownValues = () => {
        // Generate years for dropdown. Should start 10 years back and to to this year + 1
        const currentYear = (new Date()).getFullYear();
        const years = Array.from({ length: 11 }, (_, i) => currentYear - i);
        const currentMonth = (new Date()).getMonth() + 1;
        this.setState({ years, currentYear, currentMonth });
    }

    setQuickDateFilter = (year, month) => {
        this.setState({ currentYear: year, currentMonth: month });
    }

    onVisitorFilterInput = (e) => {
        const val = e.target.value;
        this.setState({ visitorFilterSearch: val });
        if (this._visitorFilterTimer) clearTimeout(this._visitorFilterTimer);
        if (val && val.length >= 2) {
            this._visitorFilterTimer = setTimeout(async () => {
                const { visitorStore } = this.props.stores;
                const results = await visitorStore.load({ query: { search: val }, skipUpdate: true });
                this.setState({ visitorFilterResults: results || [] });
            }, 300);
        } else {
            this.setState({ visitorFilterResults: [] });
        }
    }

    onVisitorFilterSelect = async (visitor) => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('activeVisitor', visitor.id);
        this.setState({
            visitorFilterSelected: visitor,
            visitorFilterSearch: '',
            visitorFilterResults: [],
        });
        await this.loadAll();
    }

    onVisitorFilterClear = async () => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('activeVisitor', null);
        this.setState({
            visitorFilterSelected: null,
            visitorFilterSearch: '',
            visitorFilterResults: [],
        });
        await this.loadAll();
    }

    componentDidMount = async () => {
        this.loadAll();
        this.setDropdownValues();
        this.syncSaleSubscription = PubSub.subscribe(topics.SYNC_SALE_UPDATED, (data) => {
            this.setState({ syncSaleNotification: data });
        });
        // Hydrate visitorFilterSelected if activeVisitor is already set (e.g., from click-to-filter).
        const { saleStore, visitorStore } = this.props.stores;
        if (saleStore.activeVisitor && !this.state.visitorFilterSelected) {
            const visitors = await visitorStore.load({ query: { id: saleStore.activeVisitor }, skipUpdate: true });
            if (visitors && visitors.length > 0) {
                this.setState({ visitorFilterSelected: visitors[0] });
            }
        }
    }

    componentWillUnmount() {
        clearTimeout(this.stepDateTimer);
        if (this.syncSaleSubscription) {
            PubSub.unsubscribe(topics.SYNC_SALE_UPDATED);
        }
    }

    render() {
        const { focused, loading, years = [], currentYear, currentMonth, visitorFilterSearch, visitorFilterResults, visitorFilterSelected } = this.state;
        const {
            saleTitle,
            searchTitle,
        } = this.props;
        const { userStore, saleStore } = this.props.stores;
        // const { user, isAdmin } = userStore;
        const { user, isAdmin, customer, isCustomerAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { language = 'no' } = user;

        const { searchText, activeStatus, activeInsuranceStatus, activeUser, activePaymentMethod, activeVisitor, currentPage = 0, cutoffDate, cutoffDateEnd, formatting = true, paymentMethodText, paymentMethodsActual, paymentMethodsFilter, limit, showDeleted, myCustomers, notTransferredToTripletex, notTransferredToFiken, notTransferredToDuett, tripletexTransferredNotSent, fikenTransferredNotSent, duettTransferredNotSent, excludeSubInvoices, onlySubInvoices, onlyUnpaidWeborders, defaultCutoffActive, criticalTask, filterIds } = saleStore;
        const { sales, allPayments, searchResults, totalSales, users } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;

        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        const searchHistory = util.get(`searchHistory-allVisits`) || [];

        const { syncSaleNotification } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-top: 0px; padding-bottom: 100px;'>

                    {syncSaleNotification && <div class='mx-3 mt-2'>
                        <SyncBanner
                            type='sale'
                            updatedByName={syncSaleNotification.updatedByName}
                            onRefresh={() => {
                                this.setState({ syncSaleNotification: null });
                                this.loadAll();
                            }}
                            onDismiss={() => this.setState({ syncSaleNotification: null })}
                        />
                    </div>}

                    <div class='w-100 d-flex flex-sm-row flex-column'>
                        <div class='w-100 d-flex flex-column'>
                            {/* <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.visits-today'>Visits today</Text>
                            </div> */}
                            <div class={`d-flex flex-column mx-3 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div class='d-flex flex-row justify-content-between'>
                                    <div class='input-group input-group-sm w-50'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-magnifying-glass'></i>
                                        </div>
                                        <input
                                            class={`form-control ${!searchText ? 'rounded-pill-right' : ''}`}
                                            type='text'
                                            value={searchText || ''}
                                            placeholder={`${searchTitle}`}
                                            onInput={this.searchInput}
                                            onKeyDown={this.handleKeyDown}
                                            onFocus={this.setFocus}
                                            ref={c => this.searchInputField = c}
                                        />
                                        {searchText && <>
                                            <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                                <i class='fa-solid fa-circle-xmark'></i>
                                            </button>
                                        </>}
                                    </div>

                                    <div>
                                        <button
                                            class={`btn btn-sm ${loading ? 'btn-success' : 'btn-outline-success'} rounded-pill ms-2 ${loading ? 'opacity-75' : ''}`}
                                            type='button'
                                            onClick={this.loadAll}
                                            disabled={loading}
                                        >
                                            <nobr>
                                                <i class={`fa-solid fa-rotate ${loading ? 'fa-spin' : ''} me-1`}></i>
                                                {loading ? <Text id='dyrejournal.loading'>Loading...</Text> : <Text id='dyrejournal.reload'>Reload</Text>}
                                            </nobr>
                                        </button>
                                    </div>

                                    <div class='input-group input-group-sm w-10'>
                                        <select class='form-control rounded-pill text-center ms-2' onChange={this.onChooseLimit} value={limit}>
                                            <option value=''><Text id='dyrejournal.select-limit'>-- Limit --</Text></option>
                                            <option value='1'>1</option>
                                            <option value='5'>5</option>
                                            <option value='10'>10</option>
                                            <option value='25'>25</option>
                                            <option value='50'>50</option>
                                            <option value='75'>75</option>
                                            <option value='100'>100</option>
                                            <option value='500'>500</option>
                                            <option value='1000'>1000</option>
                                        </select>
                                    </div>

                                    <div class='d-flex flex-row'>
                                        <select class='form-control form-control-sm rounded-pill ms-2' onChange={this.onChooseInsuranceStatus} value={activeInsuranceStatus ?? ''}>
                                            <option value=''><Text id='dyrejournal.select-insurance-status'>-- Insurance status --</Text></option>
                                            {[0, 1, 2, 3, 4].map((e, idx) => {
                                                const insuranceStatusKey = saleStore.getInsuranceStatusById(e);
                                                const insuranceStatusIcon = saleStore.getInsuranceStatusIcon(insuranceStatusKey);
                                                return (<>
                                                    <option value={e}><i class={insuranceStatusIcon} /> {util.ucfirst(saleStore.getInsuranceStatusById(e, language))}</option>
                                                </>);
                                            })}
                                        </select>

                                        <select class='form-control form-control-sm rounded-pill ms-2' onChange={this.onChooseStatus} value={activeStatus ?? ''}>
                                            <option value=''><Text id='dyrejournal.select-sale-status'>-- Choose status --</Text></option>
                                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 1001, 1002, 1003, 1005, 1004, 1006, 9999].map((e, idx) => {
                                                const saleStatus = saleStore.getSaleStatusById(e);
                                                const saleStatusIcon = saleStore.getSaleStatusIcon(saleStatus);
                                                return (<>
                                                    <option value={e}><i class={saleStatusIcon} /> {util.ucfirst(saleStore.getSaleStatusById(e, language))}</option>
                                                </>);
                                            })}
                                        </select>

                                        <select class='form-control form-control-sm rounded-pill ms-2' onChange={this.onChoosePaymentMethod} value={activePaymentMethod ?? ''}>
                                            <option value=''><Text id='dyrejournal.select-sale-payment-method'>-- Choose payment method --</Text></option>
                                            {Object.keys(paymentMethodsFilter).map((e, idx) => {
                                                const paymentMethod = saleStore.getPaymentMethodText(e);
                                                return (<>
                                                    <option value={e}>{paymentMethod}</option>
                                                </>);
                                            })}
                                        </select>

                                        <div class='position-relative ms-2' style='min-width: 200px;'>
                                            {!visitorFilterSelected && (
                                                <input
                                                    type='text'
                                                    class='form-control form-control-sm rounded-pill'
                                                    placeholder='Søk eier...'
                                                    value={visitorFilterSearch || ''}
                                                    onInput={this.onVisitorFilterInput}
                                                />
                                            )}
                                            {!!visitorFilterSelected && (
                                                <div class='d-flex align-items-center border rounded-pill px-2 py-1'>
                                                    <span class='me-2'>
                                                        <strong>{visitorFilterSelected.firstname} {visitorFilterSelected.lastname}</strong>
                                                    </span>
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-link p-0 ms-auto'
                                                        onClick={this.onVisitorFilterClear}
                                                    >
                                                        <i class='fa-solid fa-xmark' />
                                                    </button>
                                                </div>
                                            )}
                                            {visitorFilterResults && visitorFilterResults.length > 0 && (
                                                <div class='list-group position-absolute shadow' style='z-index: 1050; top: 100%; left: 0; right: 0;'>
                                                    {visitorFilterResults.slice(0, 8).map(v => (
                                                        <button
                                                            key={v.id}
                                                            type='button'
                                                            class='list-group-item list-group-item-action'
                                                            onClick={() => this.onVisitorFilterSelect(v)}
                                                        >
                                                            <strong>{v.firstname} {v.lastname}</strong>
                                                            {v.cellphone ? <span class='ms-2 text-body-secondary'>{v.cellphone}</span> : null}
                                                            {v.email ? <span class='ms-2 text-body-secondary'>{v.email}</span> : null}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <select class='form-control form-control-sm rounded-pill ms-2' onChange={this.onChooseSaleUser} value={activeUser ?? ''}>
                                            <option value=''><Text id='dyrejournal.select-sale-user'>-- Choose sales person --</Text></option>
                                            {users.map((e, idx) => {
                                                return (<>
                                                    <option value={e.id}>{`${e.firstname} ${e.lastname}`}</option>
                                                </>);
                                            })}
                                        </select>

                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${activeStatus === 99 ? 'info' : 'outline-info'} rounded-pill ms-2`}
                                            onClick={this.onClickStatus}
                                            data-id={99}
                                        >
                                            <nobr>
                                                <i class='' /> <Text id='dyrejournal.debitor-list'>Debitor list</Text>
                                            </nobr>
                                        </button>

                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${activeStatus === UNCONFIRMED_STATUS ? 'warning' : 'outline-warning'} rounded-pill ms-2`}
                                            onClick={this.onClickStatus}
                                            data-id={UNCONFIRMED_STATUS}
                                        >
                                            <nobr>
                                                <i class='fa-duotone fa-calendar-circle-exclamation' /> <Text id='dyrejournal.unconfirmed-bookings'>Unconfirmed bookings</Text>
                                            </nobr>
                                        </button>

                                    </div>
                                </div>

                                <div class='d-flex flex-row justify-content-start w-100 mt-1'>
                                    {focused && <>
                                        {searchHistory.length > 0 && <>
                                            {searchHistory.map((item, idx) => {
                                                return (<>
                                                    <small class='font-weight-lighter mx-1' onClick={() => this.searchFromHistory(item)} style='cursor: pointer;'>
                                                        "{item}"
                                                    </small>
                                                </>);
                                            })}
                                        </>}
                                    </>}
                                </div>

                                {/* Row 1: Date selectors (left) + Filter buttons (right) */}
                                <div class='d-flex flex-row flex-wrap justify-content-between align-items-center mt-1'>
                                    {/* Left: Date selectors */}
                                    <div class='d-flex flex-row flex-wrap align-items-center gap-2'>
                                        <div class='input-group input-group-sm' style='width: auto;'>
                                            <div class='input-group-text rounded-pill-left'>
                                                <i class='fa-regular fa-timer me-2'></i> Fra
                                            </div>
                                            <input
                                                class={`form-control rounded-${cutoffDate ? 'none' : 'pill-right'}`}
                                                type='date'
                                                onInput={this.changeCutoffDate}
                                                value={cutoffDate || ''}
                                            />
                                            {cutoffDate && <>
                                                <button class='btn btn btn-danger rounded-pill-right' type='button' onClick={this.clearCutoffDate}>
                                                    <i class='fa-solid fa-circle-xmark'></i>
                                                </button>
                                            </>}
                                        </div>
                                        <div class='input-group input-group-sm' style='width: auto;'>
                                            <div class='input-group-text rounded-pill-left'>
                                                <i class='fa-regular fa-timer me-2'></i> Til
                                            </div>
                                            <input
                                                class={`form-control rounded-${cutoffDateEnd ? 'none' : 'pill-right'}`}
                                                type='date'
                                                onInput={this.changeCutoffDateEnd}
                                                value={cutoffDateEnd || ''}
                                            />
                                            {cutoffDateEnd && <>
                                                <button class='btn btn-sm btn-danger rounded-pill-right' type='button' onClick={this.clearCutoffDateEnd}>
                                                    <i class='fa-solid fa-circle-xmark'></i>
                                                </button>
                                            </>}
                                        </div>
                                        <div class='d-flex flex-row input-group input-group-sm' style='width: auto; min-width: 14rem;'>
                                            <span class='input-group-text rounded-pill-left'>
                                                <i class='fa-regular fa-forward-fast'></i>
                                            </span>
                                            <select class='form-control form-control-sm rounded-none ms-0' style='min-width: 4rem;' onChange={this.changeYear} value={currentYear || 0}>
                                                <option value='0'><Text id='dyrejournal.select-year'>-- Choose year --</Text></option>
                                                {years.map((year, idx) => {
                                                    return (<>
                                                        <option value={year}>{year}</option>
                                                    </>);
                                                })}
                                            </select>
                                            <select class='form-control form-control-sm rounded-pill-right' style='min-width: 5rem;' onChange={this.changeMonth} value={currentMonth || 0}>
                                                <option value='0'><Text id='dyrejournal.select-month'>-- Choose month --</Text></option>
                                                {Array.from({ length: 12 }, (_, i) => i + 1).map((month, idx) => {
                                                    const monthName = (new Date(2000, month - 1, 1)).toLocaleString('no-NO', { month: 'long' });
                                                    return (<>
                                                        <option value={month}>{monthName}</option>
                                                    </>);
                                                })}
                                            </select>
                                        </div>
                                        <div class='btn-group btn-group-sm' role='group'>
                                            <button
                                                class='btn btn-sm btn-outline-secondary rounded-pill-left'
                                                type='button'
                                                onClick={this.onClickStepDateRange}
                                                data-direction='-1'
                                                disabled={!cutoffDate && !cutoffDateEnd}
                                                title='Forrige periode'
                                            >
                                                <i class='fa-solid fa-chevron-left' />
                                            </button>
                                            <button class='btn btn-sm btn-outline-secondary rounded-none' type='button' onClick={this.onClickGoToToday} title='Gå til i dag'>
                                                <nobr>
                                                    <i class='fa-solid fa-calendar-day me-1'></i>
                                                    I dag
                                                </nobr>
                                            </button>
                                            <button
                                                class='btn btn-sm btn-outline-secondary rounded-pill-right'
                                                type='button'
                                                onClick={this.onClickStepDateRange}
                                                data-direction='1'
                                                disabled={!cutoffDate && !cutoffDateEnd}
                                                title='Neste periode'
                                            >
                                                <i class='fa-solid fa-chevron-right' />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Right: Filter buttons */}
                                    <div class='d-flex flex-row flex-wrap align-items-center gap-1'>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn${formatting ? '' : '-outline'}-primary rounded-pill ms-2`}
                                            onClick={this.onClickToggleFormatting}
                                        >
                                            <nobr><i class='fa-duotone fa-list' /> <Text id='dyrejournal.toggle-formatting'>Formatting</Text></nobr>
                                        </button>
                                        <button
                                            class={`btn btn-sm btn-${showDeleted ? '' : 'outline-'}secondary rounded-pill ms-2`}
                                            type='button'
                                            onClick={this.toggleShowDeleted}
                                            title={showDeleted ? 'Skjul slettede' : 'Vis slettede'}
                                        >
                                            <nobr>
                                                <i class={`fa-duotone fa-${showDeleted ? 'eye-slash' : 'eye'} me-1`} />
                                                Slettede
                                            </nobr>
                                        </button>
                                        {customer?.featureFlags?.sharedSales > 0 && user?.customerIds?.length > 1 && <>
                                            <button
                                                class={`btn btn-sm btn-${myCustomers ? '' : 'outline-'}info rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleMyCustomers}
                                                title='Vis salg fra alle firmaene dine'
                                            >
                                                <nobr>
                                                    <i class='fa-duotone fa-building-user me-1' />
                                                    <Text id='dyrejournal.all-my-customers'>All companies</Text>
                                                </nobr>
                                            </button>
                                        </>}
                                        {customer?.featureFlags?.tripletex > 0 && <>
                                            <button
                                                class={`btn btn-sm btn-${notTransferredToTripletex ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleNotTransferredToTripletex}
                                                title='Vis kun salg som IKKE er overført til Tripletex'
                                            >
                                                <nobr>
                                                    <i class={`fa-duotone fa-filter${notTransferredToTripletex ? '-circle-xmark' : ''} me-1`} />
                                                    Tripletex ikke overf.
                                                </nobr>
                                            </button>
                                            <button
                                                class={`btn btn-sm btn-${tripletexTransferredNotSent ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleTripletexTransferredNotSent}
                                                title='Vis kun salg overført til Tripletex, men ikke sendt til kunde'
                                            >
                                                <nobr>
                                                    <i class='fa-solid fa-paper-plane me-1' style={tripletexTransferredNotSent ? '' : 'opacity: 0.5;'} />
                                                    <Text id='dyrejournal.sale.tripletex-not-sent'>Tripletex: ikke sendt</Text>
                                                </nobr>
                                            </button>
                                        </>}
                                        {customer?.featureFlags?.fiken > 0 && <>
                                            <button
                                                class={`btn btn-sm btn-${notTransferredToFiken ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleNotTransferredToFiken}
                                                title='Vis kun salg som IKKE er overført til Fiken'
                                            >
                                                <nobr>
                                                    <i class={`fa-duotone fa-filter${notTransferredToFiken ? '-circle-xmark' : ''} me-1`} />
                                                    Fiken ikke overf.
                                                </nobr>
                                            </button>
                                            <button
                                                class={`btn btn-sm btn-${fikenTransferredNotSent ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleFikenTransferredNotSent}
                                                title='Vis kun salg overført til Fiken, men ikke sendt til kunde'
                                            >
                                                <nobr>
                                                    <i class='fa-solid fa-paper-plane me-1' style={fikenTransferredNotSent ? '' : 'opacity: 0.5;'} />
                                                    <Text id='dyrejournal.sale.fiken-not-sent'>Fiken: ikke sendt</Text>
                                                </nobr>
                                            </button>
                                        </>}
                                        {customer?.featureFlags?.duett > 0 && <>
                                            <button
                                                class={`btn btn-sm btn-${notTransferredToDuett ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleNotTransferredToDuett}
                                                title='Vis kun fakturasalg som IKKE er overført til Duett'
                                            >
                                                <nobr>
                                                    <i class={`fa-duotone fa-filter${notTransferredToDuett ? '-circle-xmark' : ''} me-1`} />
                                                    Duett ikke overf.
                                                </nobr>
                                            </button>
                                            <button
                                                class={`btn btn-sm btn-${duettTransferredNotSent ? '' : 'outline-'}warning rounded-pill ms-2`}
                                                type='button'
                                                onClick={this.toggleDuettTransferredNotSent}
                                                title='Vis kun salg overført til Duett der Duett ikke har bekreftet utsending til kunde'
                                            >
                                                <nobr>
                                                    <i class='fa-solid fa-paper-plane me-1' style={duettTransferredNotSent ? '' : 'opacity: 0.5;'} />
                                                    <Text id='dyrejournal.sale.duett-not-sent'>Duett: ikke sendt</Text>
                                                </nobr>
                                            </button>
                                        </>}
                                        <button
                                            class={`btn btn-sm btn-${excludeSubInvoices ? '' : 'outline-'}secondary rounded-pill ms-2`}
                                            type='button'
                                            onClick={this.toggleExcludeSubInvoices}
                                            title='Skjul underbilag (salg som er del av en samlefaktura)'
                                        >
                                            <nobr>
                                                <i class={`fa-duotone fa-eye${excludeSubInvoices ? '-slash' : ''} me-1`} />
                                                <Text id='dyrejournal.hide-sub-invoices'>Skjul underbilag</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            class={`btn btn-sm btn-${onlySubInvoices ? '' : 'outline-'}secondary rounded-pill ms-2`}
                                            type='button'
                                            onClick={this.toggleOnlySubInvoices}
                                            title='Vis kun underbilag (salg knyttet til samlefaktura)'
                                        >
                                            <nobr>
                                                <i class='fa-duotone fa-link me-1' />
                                                <Text id='dyrejournal.only-sub-invoices'>Kun underbilag</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm rounded-pill ms-2 ${onlyUnpaidWeborders ? 'btn-warning' : 'btn-outline-warning'}`}
                                            onClick={this.toggleOnlyUnpaidWeborders}
                                        >
                                            <i class='fa-solid fa-cart-shopping me-1' />
                                            <Text id='dyrejournal.only-unpaid-weborders'>Only unpaid web orders</Text>
                                        </button>
                                    </div>
                                </div>

                                {/* Quick date presets */}
                                <div class='d-flex flex-row flex-wrap align-items-center gap-1 mt-1'>
                                    <small class='text-body-secondary me-1'>
                                        <i class='fa-duotone fa-calendar-range' />
                                        <Text id='dyrejournal.quick-date-label'>Hurtigvalg:</Text>
                                    </small>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill py-0'
                                        onClick={this.onClickPresetThisMonth}
                                    >
                                        <Text id='dyrejournal.preset-this-month'>Denne måned</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill py-0'
                                        onClick={this.onClickPresetPreviousMonth}
                                    >
                                        <Text id='dyrejournal.preset-previous-month'>Forrige måned</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill py-0'
                                        onClick={this.onClickPresetThisYear}
                                    >
                                        <Text id='dyrejournal.preset-this-year'>I år</Text>
                                    </button>
                                </div>

                                {defaultCutoffActive && <div class='alert alert-info py-1 px-2 mt-1 mb-0' role='alert' style='font-size: 0.85em;'>
                                    <i class='fa-duotone fa-circle-info me-1' />
                                    <Text id='dyrejournal.default-cutoff-banner'>Viser kun siste 180 dager. Sett en dato eller velg "I år" for å se eldre salg.</Text>
                                </div>}

                                {/* Row 2: Pagination + Download/Output buttons */}
                                <div class='d-flex flex-row justify-content-between mt-1'>
                                    <div class='d-flex flex-row justify-content-start'>
                                        <Pagination total={totalSales} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} minimalistic={true} />
                                    </div>

                                    <div class='d-flex flex-row justify-content-end'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-primary rounded-pill ms-2'
                                            onClick={this.onClickPrintSales}
                                        >
                                            <nobr><i class='fa-duotone fa-print' /> <Text id='dyrejournal.print-sales'>Print sales</Text></nobr>
                                        </button>
                                        <a
                                            href={`${apiServer}${saleStore.api?.load?.url}csv?jwtToken=${jwtToken}&status=${activeStatus || ''}&user=${activeUser || ''}&paymentMethod=${activePaymentMethod || ''}&visitor=${activeVisitor || ''}&cutoffDate=${cutoffDate || ''}&cutoffDateEnd=${cutoffDateEnd || ''}&search=${searchText || ''}`}
                                            target='_blank'
                                            class={`btn btn-sm btn-outline-primary rounded-pill ms-2 pt-2`}
                                        >
                                            <nobr><i class='fa-duotone fa-download' /> <Text id='dyrejournal.download-csv'>Download CSV</Text></nobr>
                                        </a>
                                        {customer?.featureFlags?.tripletex && <>
                                            <a
                                                href={`${apiServer}/api/integrations/tripletex/invoices-csv?jwtToken=${jwtToken}&status=${activeStatus || ''}&user=${activeUser || ''}&paymentMethod=${activePaymentMethod || ''}&visitor=${activeVisitor || ''}&startDate=${cutoffDate || ''}&endDate=${cutoffDateEnd || ''}`}
                                                target='_blank'
                                                class={`btn btn-sm btn-outline-success rounded-pill ms-2 pt-2`}
                                                title='Export til Tripletex (CSV)'
                                            >
                                                <nobr><i class='fa-duotone fa-file-invoice' /> Tripletex CSV</nobr>
                                            </a>
                                        </>}
                                        <a
                                            href={`${apiServer}/api/integrations/gbat10/invoices-csv?jwtToken=${jwtToken}&status=${activeStatus || ''}&paymentMethod=${activePaymentMethod || ''}&visitor=${activeVisitor || ''}&startDate=${cutoffDate || ''}&endDate=${cutoffDateEnd || ''}`}
                                            target='_blank'
                                            class={`btn btn-sm btn-outline-info rounded-pill ms-2 pt-2`}
                                            title='GBAT10 format (30 kolonner, dobbelt bokføring)'
                                        >
                                            <nobr><i class='fa-duotone fa-file-spreadsheet' /> GBAT10</nobr>
                                        </a>
                                    </div>
                                </div>

                                {activeStatus === 2 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-unpaid'>Showing all unpaid sales. If you want to copy/paste into Excel. Remove the formatting with the "Formatting" button in the top right corner.</Text>
                                    </div>
                                </>}
                                {activeStatus === 0 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-inprogress'>Showing all sales in progress. No payment method chosen yet.</Text>
                                        <hr />
                                        <strong>Merk pågående salg som samlefaktura</strong>
                                        <div class='text-body-secondary mb-2'>
                                            <small>Velg en periode og trykk "Merk alle pågående" for å merke alle pågående salg i perioden som samlefaktura.</small>
                                        </div>
                                        <div class='d-flex align-items-end flex-wrap gap-2'>
                                            <div>
                                                <label class='form-label mb-0'><small>Fra</small></label>
                                                <input
                                                    type='date'
                                                    class='form-control form-control-sm'
                                                    value={saleStore.collectiveInvoiceCutoffDate}
                                                    onInput={this.onChangeBulkMarkCutoffDate}
                                                />
                                            </div>
                                            <div>
                                                <label class='form-label mb-0'><small>Til</small></label>
                                                <input
                                                    type='date'
                                                    class='form-control form-control-sm'
                                                    value={saleStore.collectiveInvoiceCutoffDateEnd}
                                                    onInput={this.onChangeBulkMarkCutoffDateEnd}
                                                />
                                            </div>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-info rounded-pill'
                                                onClick={this.onClickBulkMarkCollectiveInvoice}
                                                disabled={this.state.bulkMarkInProgress}
                                            >
                                                {this.state.bulkMarkInProgress ? <>
                                                    <span class='spinner-border spinner-border-sm me-1' /> Merker...
                                                </> : <>
                                                    <i class='fa-solid fa-tags me-1' /> Merk alle pågående
                                                </>}
                                            </button>
                                        </div>
                                        {this.state.bulkMarkResult && this.state.bulkMarkResult.updatedCount !== undefined && <>
                                            <div class='alert alert-success mt-2 mb-0 py-1 px-2'>
                                                {this.state.bulkMarkResult.updatedCount} salg ble merket som samlefaktura.
                                            </div>
                                        </>}
                                        {this.state.bulkMarkResult && this.state.bulkMarkResult.error && <>
                                            <div class='alert alert-danger mt-2 mb-0 py-1 px-2'>
                                                {this.state.bulkMarkResult.error}
                                            </div>
                                        </>}
                                    </div>
                                </>}
                                {activeStatus === 1 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-paid'>Showing all paid sales.</Text>
                                    </div>
                                </>}
                                {activeStatus === 3 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-partial'>Showing all sales with payment method partial.</Text>
                                    </div>
                                </>}
                                {activeStatus === 4 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-invoice'>Showing all sales with payment method invoice.</Text>
                                    </div>
                                </>}
                                {activeStatus === 5 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-account'>Showing all sales with payment method account.</Text>
                                    </div>
                                </>}
                                {activeStatus === 6 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-creditnote'>Showing all sales credit notes. A credit note is linked to another sale with invoice credited status.</Text>
                                    </div>
                                </>}
                                {activeStatus === 8 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-salecredited'>Showing all sales that are credited with a credit note.</Text>
                                    </div>
                                </>}
                                {activeStatus === 1001 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-priceoffer'>Showing all price offers to a visitor.</Text>
                                    </div>
                                </>}
                                {activeStatus === 1002 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-archived'>Showing all sales that has been archived. These sales are usually only sketches for your company.</Text>
                                    </div>
                                </>}
                                {activeStatus === 1003 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-collectiveinvoice'>Showing all sales that are marked as collective invoice. Later when they are sent to a visitor, they will change status.</Text>
                                    </div>
                                </>}
                                {activeStatus === 1004 && <>
                                    <div class='alert alert-secondary mt-2' role='alert'>
                                        <Text id='dyrejournal.all-lost'>Showing all sales that are marked as lost. They will no longer be a part of your turnover totals. All lost sales are marked as a cost for you clinic and you can get tax return for this cost.</Text>
                                    </div>
                                </>}

                                {saleStore.hasActiveFilters && <>
                                    <div class='alert alert-warning mt-2 py-1 px-2 rounded-lg' role='alert'>
                                        <div class='d-flex align-items-center justify-content-between'>
                                            <span class='d-flex align-items-center flex-wrap'>
                                                <i class='fa-solid fa-filter me-2' />
                                                <small class='me-1'><Text id='dyrejournal.filters-active'>Filters active</Text>:</small>
                                                {searchText && <span class='badge bg-secondary me-1'>Søk: "{searchText}"</span>}
                                                {activeStatus !== null && <span class='badge bg-secondary me-1'>{saleStore.getSaleStatusById(activeStatus, language)}</span>}
                                                {activeInsuranceStatus !== null && <span class='badge bg-secondary me-1'>{saleStore.getInsuranceStatusById(activeInsuranceStatus, language)}</span>}
                                                {activePaymentMethod && <span class='badge bg-secondary me-1'>{saleStore.getPaymentMethodText(activePaymentMethod, language)}</span>}
                                                {activeVisitor && visitorFilterSelected && <span class='badge bg-secondary me-1'>{visitorFilterSelected.firstname} {visitorFilterSelected.lastname}</span>}
                                                {activeUser && <span class='badge bg-secondary me-1'>{users.find(u => u.id === activeUser)?.firstname || activeUser}</span>}
                                                {cutoffDate && <span class='badge bg-secondary me-1'>Fra: {cutoffDate}</span>}
                                                {cutoffDateEnd && <span class='badge bg-secondary me-1'>Til: {cutoffDateEnd}</span>}
                                                {showDeleted && <span class='badge bg-secondary me-1'>Viser slettede</span>}
                                                {notTransferredToTripletex && <span class='badge bg-warning text-dark me-1'>Ikke overført (Tripletex)</span>}
                                                {tripletexTransferredNotSent && <span class='badge bg-warning text-dark me-1'>Tripletex: ikke sendt</span>}
                                                {notTransferredToFiken && <span class='badge bg-warning text-dark me-1'>Ikke overført (Fiken)</span>}
                                                {fikenTransferredNotSent && <span class='badge bg-warning text-dark me-1'>Fiken: ikke sendt</span>}
                                                {notTransferredToDuett && <span class='badge bg-warning text-dark me-1'>Ikke overført (Duett)</span>}
                                                {duettTransferredNotSent && <span class='badge bg-warning text-dark me-1'>Duett: ikke sendt</span>}
                                                {criticalTask && <span class='badge bg-danger me-1'><i class='fa-duotone fa-list-check me-1' />Kritisk oppgave: {CRITICAL_TASK_LABELS[criticalTask] || criticalTask}</span>}
                                                {Array.isArray(filterIds) && filterIds.length > 0 && <span class='badge bg-danger me-1'>{filterIds.length} utvalgte salg</span>}
                                                {currentPage > 1 && <span class='badge bg-secondary me-1'>Side {currentPage}</span>}
                                            </span>
                                            <button class='btn btn-sm btn-warning rounded-pill' onClick={this.onClearAllFilters}>
                                                <i class='fa-solid fa-times me-1' />
                                                <Text id='dyrejournal.clear-all-filters'>Clear all filters</Text>
                                            </button>
                                        </div>
                                        <div class='small mt-1 pt-1 border-top border-warning-subtle'>
                                            <i class='fa-duotone fa-circle-info me-1' />
                                            {(() => {
                                                const parts = [];
                                                if (criticalTask) {
                                                    parts.push(`for den kritiske oppgaven «${CRITICAL_TASK_LABELS[criticalTask] || criticalTask}»`);
                                                }
                                                if (activeStatus !== null) {
                                                    parts.push(`med status «${saleStore.getSaleStatusById(activeStatus, language)}»`);
                                                }
                                                if (activePaymentMethod) {
                                                    parts.push(`betalt med ${saleStore.getPaymentMethodText(activePaymentMethod, language)}`);
                                                }
                                                if (activeInsuranceStatus !== null) {
                                                    parts.push(`forsikringsstatus ${saleStore.getInsuranceStatusById(activeInsuranceStatus, language)}`);
                                                }
                                                if (visitorFilterSelected) {
                                                    const navn = `${visitorFilterSelected.firstname || ''} ${visitorFilterSelected.lastname || ''}`.trim();
                                                    if (navn) parts.push(`for kunden ${navn}`);
                                                }
                                                if (activeUser) {
                                                    const u = users.find(x => x.id === activeUser);
                                                    parts.push(`registrert av ${u?.firstname || activeUser}`);
                                                }
                                                if (cutoffDate && cutoffDateEnd) {
                                                    parts.push(`i perioden ${cutoffDate} til ${cutoffDateEnd}`);
                                                } else if (cutoffDate) {
                                                    parts.push(`fra ${cutoffDate}`);
                                                } else if (cutoffDateEnd) {
                                                    parts.push(`til ${cutoffDateEnd}`);
                                                }
                                                if (searchText) {
                                                    parts.push(`som matcher søket «${searchText}»`);
                                                }
                                                const extras = [];
                                                if (showDeleted) extras.push('inkluderer slettede salg');
                                                if (notTransferredToTripletex) extras.push('ikke overført til Tripletex');
                                                if (tripletexTransferredNotSent) extras.push('overført til Tripletex men ikke sendt');
                                                if (notTransferredToFiken) extras.push('ikke overført til Fiken');
                                                if (fikenTransferredNotSent) extras.push('overført til Fiken men ikke sendt');
                                                if (notTransferredToDuett) extras.push('ikke overført til Duett');
                                                if (duettTransferredNotSent) extras.push('overført til Duett men utsending ikke bekreftet');

                                                const truncated = totalSales > limit;
                                                const shownCount = Math.min(limit, Math.max(totalSales - (currentPage - 1) * limit, 0));
                                                const lead = truncated
                                                    ? `Viser ${shownCount} av ${totalSales} salg`
                                                    : `Du ser alle ${totalSales || 0} salg`;
                                                let sentence = `${lead}${parts.length ? ' ' + parts.join(', ') : ''}`;
                                                if (extras.length) sentence += ` (${extras.join('; ')})`;
                                                sentence += '.';
                                                if (truncated) {
                                                    sentence += ` Øk grensen eller bla videre for å se resten.`;
                                                }
                                                return sentence;
                                            })()}
                                        </div>
                                    </div>
                                </>}

                            </div>


                            {activeStatus === UNCONFIRMED_STATUS ? <>
                                <DyrejournalUnconfirmedBookings
                                    stores={this.props.stores}
                                    loading={this.state.loading}
                                    callback={() => this.loadAll()}
                                />
                            </> : <DyrejournalAllSalesTable
                                stores={this.props.stores}
                                formatting={formatting}
                                showDelete={true}
                                limit={limit}

                                searchText={searchText}
                                activeStatus={activeStatus}
                                activeUser={activeUser}
                                activePaymentMethod={activePaymentMethod}
                                cutoffDate={cutoffDate}
                                cutoffDateEnd={cutoffDateEnd}

                                callback={() => this.loadAll()}
                                setQuickDateFilter={this.setQuickDateFilter}
                            />}

                        </div>

                    </div>

                    {totalSales > limit && activeStatus !== 99 && activeStatus !== UNCONFIRMED_STATUS && <div class='mx-3 mt-3'>
                        <div class='alert alert-warning d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 p-3' role='alert'>
                            <div>
                                <h5 class='mb-1'>
                                    <i class='fa-solid fa-triangle-exclamation me-2' />
                                    <Text id='dyrejournal.list-truncated-title' fields={{ shown: ((currentPage - 1) * limit) + Math.min(limit, totalSales - (currentPage - 1) * limit), total: totalSales }}>
                                        Du ser kun {{ shown: ((currentPage - 1) * limit) + Math.min(limit, totalSales - (currentPage - 1) * limit) }} av {{ total: totalSales }} salg
                                    </Text>
                                </h5>
                                <div class='small mb-0'>
                                    <Text id='dyrejournal.list-truncated-help' fields={{ limit, total: totalSales }}>
                                        Lista er begrenset til {{ limit }} salg per side. Bla videre med pagineringen, øk grensen, eller trykk "Vis alle" for å hente alle {{ total: totalSales }} salg i ett.
                                    </Text>
                                </div>
                            </div>
                            <div class='d-flex flex-row align-items-center gap-2'>
                                <select class='form-select form-select-sm' style='width: auto;' value={limit} onChange={this.onChooseLimit}>
                                    <option value='25'>25</option>
                                    <option value='50'>50</option>
                                    <option value='100'>100</option>
                                    <option value='250'>250</option>
                                    <option value='500'>500</option>
                                    <option value='1000'>1000</option>
                                </select>
                                <button type='button' class='btn btn-warning rounded-pill' onClick={this.onClickShowAll}>
                                    <i class='fa-solid fa-list me-1' />
                                    <Text id='dyrejournal.show-all'>Vis alle</Text>
                                </button>
                            </div>
                        </div>
                    </div>}

                    {(activeStatus === 99 || activeStatus === UNCONFIRMED_STATUS) ? <></> : <>
                        <Pagination total={totalSales} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} />
                    </>}

                </div>
            </div>
        );
    }
}

export default AllVisits;
